// The development's certificates as a grid (the demo's pmCompliance): every flat and the building down the side,
// every certificate type across, each cell coloured by the newest certificate. Click a cell to open the certificate
// with the ones it replaced underneath, or to record one where there is none. The toolbar finds a unit; the Filter
// card narrows to the units with a certificate in one state (?pmf=, the same as the tiles) and to some certificate types.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { useCan } from '../../hooks/usePermission';
import { Badge, Button, KeyValue, Sheet, SkeletonRows } from '../ui';
import { ListToolbar } from '../ui/ListToolbar';
import { FilterMenu } from '../ui/FilterMenu';
import { StatTile, FilterNote } from '../charts/StatTile';
import { CertificateForm } from '../forms/CertificateForm';
import { CELL_TONE, daysTo, pmApi } from '../../lib/propertyManagement';
import { formatDate } from '../../lib/viz';
import type { ComplianceGridCell, ComplianceGridRow, GridCellStatus } from '../../types/api';

type Counted = Exclude<GridCellStatus, 'NOT_APPLICABLE'>;
const TILE_ORDER: Counted[] = ['VALID', 'DUE_SOON', 'EXPIRED', 'NOT_RECORDED'];
const short = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' });

export function ComplianceGridPanel({ propertyId, filter, onFilter, focus, onFocus }: {
  propertyId: string;
  /** ?pmf= — only the rows with a cell in this state. */
  filter: Counted | null; onFilter: (f: Counted | null) => void;
  /** ?unit=&ctype= — the cell to open (from "Needs attention"). */
  focus: { unit: string; type: string } | null; onFocus: (f: { unit: string; type: string } | null) => void;
}) {
  const canCreate = useCan('compliance.create');
  const grid = useQuery({ queryKey: ['compliance-grid', propertyId], queryFn: () => pmApi.grid(propertyId) });
  const [recording, setRecording] = useState<{ unitId: string | null; type: string } | null>(null);
  const [search, setSearch] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const g = grid.data;

  const needle = search.trim().toLowerCase();
  const rows = useMemo(() => (g?.rows ?? []).filter((r) => (!filter || Object.values(r.cells).some((c) => c.status === filter)) && (!needle || r.label.toLowerCase().includes(needle))), [g, filter, needle]);
  const columns = useMemo(() => (g?.columns ?? []).filter((c) => !types.length || types.includes(c.key)), [g, types]);
  const open = focus && g ? (() => {
    const row = g.rows.find((r) => r.key === focus.unit);
    const cell = row?.cells[focus.type];
    return row && cell ? { row, type: focus.type, cell } : null;
  })() : null;
  // A linked cell with nothing recorded goes straight to the form (when the user may record one).
  const toRecord = recording ?? (open && !open.cell.certificateId && canCreate && open.cell.status !== 'NOT_APPLICABLE' ? { unitId: open.row.unitId, type: open.type } : null);

  if (grid.isLoading) return <div className="surface p-4"><SkeletonRows rows={6} cols={5} /></div>;
  if (!g) return <div className="surface py-16 text-center text-sm text-ink-400">The certificates could not be loaded.</div>;
  const labelOf = (t: string) => g.columns.find((c) => c.key === t)?.label ?? t;

  return (
    <div className="space-y-3">
      <div className="tile-row">
        {TILE_ORDER.map((k) => (
          <StatTile key={k} compact label={CELL_TONE[k]!.label} value={g.counts[k]}
            color={g.counts[k] ? (k === 'VALID' ? 'var(--color-bronze-600)' : k === 'NOT_RECORDED' ? undefined : CELL_TONE[k]!.color) : undefined}
            active={filter === k} onClick={() => onFilter(filter === k ? null : k)} />
        ))}
      </div>
      {filter && <FilterNote label={`Only the places with something ${CELL_TONE[filter]!.label.toLowerCase()}`} count={rows.length} onClear={() => onFilter(null)} />}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-ink-500">
        <span className="font-semibold uppercase tracking-[0.14em] text-ink-400">Key</span>
        {TILE_ORDER.map((k) => <span key={k} className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full" style={{ background: CELL_TONE[k]!.color }} />{CELL_TONE[k]!.label}</span>)}
        <span className="text-ink-400">— does not apply</span>
        <span className="hidden text-ink-400 sm:inline">· Click a cell to open or record a certificate.</span>
      </div>

      <div className="surface overflow-hidden">
        <ListToolbar search={search} onSearch={setSearch} searchPlaceholder="Find a unit…"
          filters={<FilterMenu value={{ status: filter ? [filter] : [], types }} onChange={(n) => { onFilter((n.status?.[0] as Counted | undefined) ?? null); setTypes(n.types ?? []); }} groups={[
            { key: 'status', label: 'Status', single: true, options: TILE_ORDER.map((k) => ({ value: k, label: `${CELL_TONE[k]!.label} (${g.counts[k]})` })) },
            { key: 'types', label: 'Certificates', options: g.columns.map((c) => ({ value: c.key, label: c.label })) },
          ]} />} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-max border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-ink-100 bg-ink-50/70 text-left">
                <th className="sticky left-0 z-10 bg-ink-50 px-3 py-1.5 bg-ink-50/60 text-[11.5px] font-medium uppercase tracking-wide text-ink-500">Unit</th>
                {columns.map((c) => <th key={c.key} title={`${c.label} — renews every ${c.intervalMonths} months`} className="px-2 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-400"><span className="block max-w-[110px] whitespace-normal leading-tight">{c.short}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className={`border-b border-ink-100 last:border-0 ${r.kind === 'BUILDING' ? 'bg-ink-50/60' : ''}`}>
                  <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-1.5 font-medium text-ink-800">{r.label}</td>
                  {columns.map((c) => {
                    const cell = r.cells[c.key]!;
                    if (cell.status === 'NOT_APPLICABLE') return <td key={c.key} className="px-2 py-2 text-center text-ink-300">—</td>;
                    const tone = CELL_TONE[cell.status]!;
                    const missing = cell.status === 'NOT_RECORDED';
                    const tip = missing ? `${c.label} — not recorded yet` : `${c.label} — ${tone.label}${cell.expiresOn ? `, expires ${formatDate(cell.expiresOn)}` : ''}${cell.historyCount ? ` · ${cell.historyCount} earlier` : ''}`;
                    return (
                      <td key={c.key} className="px-1.5 py-1.5">
                        <button type="button" title={tip} disabled={missing && !canCreate}
                          onClick={() => (cell.certificateId ? onFocus({ unit: r.key, type: c.key }) : setRecording({ unitId: r.unitId, type: c.key }))}
                          className={`flex w-full min-w-[92px] items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left transition hover:shadow-sm disabled:cursor-default disabled:hover:shadow-none ${missing ? 'border-ink-200 text-ink-400' : 'border-transparent text-ink-800'}`}
                          style={missing ? undefined : { background: `color-mix(in oklab, ${tone.color} 12%, transparent)` }}>
                          <i className="h-2 w-2 shrink-0 rounded-full" style={{ background: tone.color }} />
                          <span className="tabular-nums">{cell.expiresOn ? short(cell.expiresOn) : 'Record'}</span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-[12.5px] text-ink-400">No unit matches.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open?.cell.certificateId && <CertificateSheet row={open.row} type={open.type} typeLabel={labelOf(open.type)} cell={open.cell} onClose={() => onFocus(null)}
        onRenew={() => { onFocus(null); setRecording({ unitId: open.row.unitId, type: open.type }); }} />}
      {toRecord && <CertificateForm open propertyId={propertyId} preset={{ unitId: toRecord.unitId, certificateType: toRecord.type }} onClose={() => { setRecording(null); if (focus) onFocus(null); }} />}
    </div>
  );
}

/** One cell's certificate: the current one, and every one before it (the demo's certModal + "Previous"). */
export function CertificateSheet({ row, type, typeLabel, cell, onClose, onRenew }: { row: ComplianceGridRow; type: string; typeLabel: string; cell: ComplianceGridCell; onClose: () => void; onRenew: () => void }) {
  const canUpdate = useCan('compliance.update'); const canCreate = useCan('compliance.create');
  const history = useQuery({ queryKey: ['compliance-history', cell.certificateId], queryFn: () => pmApi.history(cell.certificateId!) });
  const [editing, setEditing] = useState(false);
  const all = history.data ?? [];
  const current = all.find((c) => c.current) ?? null;
  const earlier = all.filter((c) => !c.current);
  const tone = CELL_TONE[cell.status] ?? CELL_TONE.NOT_RECORDED!;
  const d = daysTo(cell.expiresOn);
  return (
    <>
      <Sheet open={!editing} onClose={onClose} eyebrow={row.kind === 'BUILDING' ? row.label : `Flat ${row.label}`} title={typeLabel} accent={tone.color}
        subtitle={d === null ? undefined : d < 0 ? `Ran out ${formatDate(cell.expiresOn!)} — ${-d} days ago` : `Runs to ${formatDate(cell.expiresOn!)} — ${d} days left`}
        headerExtra={<span className="mr-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium" style={{ color: tone.color, background: `color-mix(in oklab, ${tone.color} 12%, transparent)` }}><i className="h-1.5 w-1.5 rounded-full" style={{ background: tone.color }} />{tone.label}</span>}
        footer={<>
          {canUpdate && current && <Button variant="secondary" size="sm" icon="Pencil" onClick={() => setEditing(true)}>Edit</Button>}
          {canCreate && <Button size="sm" icon="RefreshCw" onClick={onRenew}>Record a renewal</Button>}
        </>}>
        {history.isLoading ? <SkeletonRows rows={4} cols={3} /> : current ? (
          <div className="space-y-2.5">
            <KeyValue items={[
              { label: 'Issued', value: current.issuedOn ? formatDate(current.issuedOn) : '—' }, { label: 'Expires', value: formatDate(current.expiresOn) },
              { label: 'Certificate number', value: current.reference ?? '—' }, { label: 'Carried out by', value: current.vendorName ?? '—' },
              ...(current.rating ? [{ label: type === 'EPC' ? 'EPC band' : 'Rating', value: current.rating }] : []),
              { label: 'Recorded status', value: <Badge value={current.status} /> },
            ]} />
            {current.documentUrl && <a href={current.documentUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-bronze-600 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Open the certificate</a>}
            <div>
              <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">Previous <span className="text-ink-300">· {earlier.length}</span></p>
              {earlier.length ? (
                <ul className="space-y-1.5">
                  {earlier.map((h) => (
                    <li key={h.id} className="flex items-start gap-3 rounded-xl border border-ink-100 px-3 py-2 text-[12.5px]">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-ink-300" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-ink-800">{h.issuedOn ? formatDate(h.issuedOn) : '—'} to {formatDate(h.expiresOn)}</div>
                        <div className="text-ink-500">{h.vendorName ?? '—'}{h.reference ? ` · ${h.reference}` : ''}</div>
                      </div>
                      {h.documentUrl && <a href={h.documentUrl} target="_blank" rel="noreferrer" className="text-[12px] font-medium text-bronze-600 hover:underline">Open</a>}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-[12.5px] text-ink-400">This is the first one recorded. A renewal keeps this one underneath.</p>}
            </div>
          </div>
        ) : <p className="py-8 text-center text-sm text-ink-400">This certificate could not be loaded.</p>}
      </Sheet>
      {editing && current && <CertificateForm open certificate={current} onClose={() => { setEditing(false); onClose(); }} />}
    </>
  );
}
